/**
 * Интерфейс целиком русский (<html lang="ru">), поэтому и даты форматируем по-русски,
 * не полагаясь на локаль браузера: иначе на английской системе время едет в 12-часовой
 * формат и ломает колонки показаний.
 */
const UI_LOCALE = "ru-RU";

export function getDeviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
}

export function formatInTimeZone(
  iso: string | Date,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = {
    dateStyle: "medium",
    timeStyle: "short",
  },
): string {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return new Intl.DateTimeFormat(UI_LOCALE, { ...options, timeZone }).format(date);
}

/** "Asia/Bangkok" → "Bangkok": то, что человек читает, а не идентификатор базы. */
export function shortZoneName(timeZone: string): string {
  const tail = timeZone.split("/").pop() ?? timeZone;
  return tail.replace(/_/g, " ");
}

export function formatTimeInZone(iso: string | Date, timeZone: string): string {
  return formatInTimeZone(iso, timeZone, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateTimePair(
  iso: string | Date,
  venueTimeZone: string | null | undefined,
): {
  deviceLabel: string;
  venueLabel: string | null;
  deviceTimeZone: string;
  venueTimeZone: string | null;
  sameZone: boolean;
} {
  const deviceTimeZone = getDeviceTimeZone();
  const venueTz = venueTimeZone && venueTimeZone.length > 0 ? venueTimeZone : null;
  const deviceLabel = `${formatInTimeZone(iso, deviceTimeZone)} (${deviceTimeZone})`;
  if (!venueTz || venueTz === deviceTimeZone) {
    return {
      deviceLabel,
      venueLabel: null,
      deviceTimeZone,
      venueTimeZone: venueTz,
      sameZone: true,
    };
  }
  return {
    deviceLabel,
    venueLabel: `${formatInTimeZone(iso, venueTz)} (${venueTz})`,
    deviceTimeZone,
    venueTimeZone: venueTz,
    sameZone: false,
  };
}

export function toIsoUtc(date: Date): string {
  return date.toISOString();
}

/**
 * Преобразует момент времени в ISO-строку с настенным временем и смещением указанной таймзоны.
 * Например: 2026-10-24T02:00:00Z в "Asia/Makassar" → "2026-10-24T10:00:00+08:00".
 *
 * `@event-calendar/core` при передаче `Date` берёт локальные часы браузера через `date.getHours()`.
 * Передача строки с явным смещением заставляет библиотеку разобрать часы настенного времени
 * нужного пояса и спозиционировать урок на правильный слот независимо от пояса устройства.
 */
export function toZonedIsoString(date: Date | string, timeZone: string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const parts = zonedDateTimeParts(d, timeZone);
  const pad = (n: number) => String(n).padStart(2, "0");
  const offset = utcOffsetLabel(d, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}:${pad(parts.second)}${offset}`;
}

export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60_000);
}

export function startOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function endOfLocalDay(date: Date): Date {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
}

export function startOfLocalWeek(date: Date, weekStartsOn = 1): Date {
  const d = startOfLocalDay(date);
  const day = d.getDay();
  const diff = (day - weekStartsOn + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

export function endOfLocalWeek(date: Date, weekStartsOn = 1): Date {
  const start = startOfLocalWeek(date, weekStartsOn);
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  return end;
}

interface CalendarDateParts {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

interface ZonedDateTimeParts extends CalendarDateParts {
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
}

function calendarDateParts(date: Date, timeZone: string): CalendarDateParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return { year: value("year"), month: value("month"), day: value("day") };
}

function zonedDateTimeParts(date: Date, timeZone: string): ZonedDateTimeParts {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value);
  return {
    year: value("year"),
    month: value("month"),
    day: value("day"),
    hour: value("hour"),
    minute: value("minute"),
    second: value("second"),
  };
}

/**
 * Смещение зоны от UTC в минутах на указанный момент. Одна формула на весь файл: показания
 * `Intl` в зоне минус сам момент — то, на сколько стрелки площадки опережают Гринвич.
 */
export function utcOffsetMinutes(date: Date, timeZone: string): number {
  const shown = zonedDateTimeParts(date, timeZone);
  const shownAsUtc = Date.UTC(
    shown.year,
    shown.month - 1,
    shown.day,
    shown.hour,
    shown.minute,
    shown.second,
  );
  return Math.round((shownAsUtc - date.getTime()) / 60_000);
}

/**
 * Смещение строкой `+08:00` — единственный формат, который понимает сетка календаря.
 *
 * `@event-calendar/core` разбирает `timeZone` регуляркой `([+-])(\d{2}):(\d{2})$` и всё, что ей
 * не подошло, молча заменяет на часовой пояс устройства (`src/storage/derived.js`). Поэтому
 * «Asia/Makassar» в этой опции означал ровно «время браузера»: сетка на Бали открытая из Москвы
 * рисовала уроки на пять часов не там, где они есть.
 *
 * Смещение берётся на конкретный момент, а не «вообще»: у зоны с переводом часов оно меняется
 * посреди года. Внутри одного экрана оно всё равно одно — на границе перевода сетка ошибётся на
 * час, но ни Бали (UTC+8), ни Пхукет (UTC+7) часы не переводят.
 */
export function utcOffsetLabel(date: Date, timeZone: string): string {
  const minutes = utcOffsetMinutes(date, timeZone);
  const sign = minutes < 0 ? "-" : "+";
  const abs = Math.abs(minutes);
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${sign}${pad(Math.trunc(abs / 60))}:${pad(abs % 60)}`;
}

/**
 * Настенное время площадки → момент времени.
 *
 * На вход — `Date`, у которого **локальные** поля браузера уже содержат часы площадки: именно
 * такие даты отдаёт сетка календаря в `eventDrop`/`select` и такие собирает `datetime-local`.
 * Просто позвать у него `toISOString()` нельзя — браузер прочитал бы эти часы своим поясом.
 */
export function wallClockToIso(wallClock: Date, timeZone: string): string {
  const asUtc = Date.UTC(
    wallClock.getFullYear(),
    wallClock.getMonth(),
    wallClock.getDate(),
    wallClock.getHours(),
    wallClock.getMinutes(),
    wallClock.getSeconds(),
  );
  // Смещение зависит от момента, а момент — от смещения. Первого приближения хватает везде,
  // кроме самого часа перевода стрелок; там второй проход берёт смещение уже по ту сторону.
  const first = utcOffsetMinutes(new Date(asUtc), timeZone);
  const candidate = asUtc - first * 60_000;
  const second = utcOffsetMinutes(new Date(candidate), timeZone);
  return new Date(second === first ? candidate : asUtc - second * 60_000).toISOString();
}

/** Обратное преобразование: момент → `Date` с часами площадки в локальных полях браузера. */
export function isoToWallClock(iso: string | Date, timeZone: string): Date {
  const date = typeof iso === "string" ? new Date(iso) : iso;
  const shown = zonedDateTimeParts(date, timeZone);
  return new Date(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute, shown.second);
}

function zonedMidnight(parts: CalendarDateParts, timeZone: string): Date {
  const target = Date.UTC(parts.year, parts.month - 1, parts.day);
  return new Date(target - utcOffsetMinutes(new Date(target), timeZone) * 60_000);
}

/** UTC bounds for the calendar day that contains `date` in `timeZone`. */
export function dayRangeInTimeZone(date: Date, timeZone: string): { from: Date; to: Date } {
  const current = calendarDateParts(date, timeZone);
  const next = new Date(Date.UTC(current.year, current.month - 1, current.day + 1));
  return {
    from: zonedMidnight(current, timeZone),
    to: zonedMidnight(calendarDateParts(next, "UTC"), timeZone),
  };
}

/** Monday-to-Monday UTC bounds for the calendar week containing `date` in `timeZone`. */
export function weekRangeInTimeZone(date: Date, timeZone: string): { from: Date; to: Date } {
  const current = calendarDateParts(date, timeZone);
  const currentUtc = new Date(Date.UTC(current.year, current.month - 1, current.day));
  const offsetToMonday = (currentUtc.getUTCDay() + 6) % 7;
  const monday = new Date(currentUtc);
  monday.setUTCDate(monday.getUTCDate() - offsetToMonday);
  const nextMonday = new Date(monday);
  nextMonday.setUTCDate(nextMonday.getUTCDate() + 7);
  return {
    from: zonedMidnight(calendarDateParts(monday, "UTC"), timeZone),
    to: zonedMidnight(calendarDateParts(nextMonday, "UTC"), timeZone),
  };
}

/** First-day-to-first-day UTC bounds for the calendar month containing `date` in `timeZone`. */
export function monthRangeInTimeZone(date: Date, timeZone: string): { from: Date; to: Date } {
  const current = calendarDateParts(date, timeZone);
  const first = { ...current, day: 1 };
  const next = new Date(Date.UTC(current.year, current.month, 1));
  return {
    from: zonedMidnight(first, timeZone),
    to: zonedMidnight(calendarDateParts(next, "UTC"), timeZone),
  };
}
